import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../../service/GetTemplate';
import { endPoints } from '../../../Config';
import { ROUTE_OPTIONS } from '../../../utilities/constants';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText } from '../../../utilities/siteTranslations';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Filter options - similar to List of Failures
const failureTypeOptions = [
    { label: 'All Types', value: null },
    { label: 'Electrical Coffee', value: 'Electrical Coffee' },
    { label: 'Electrical Snack', value: 'Electrical Snack' },
    { label: 'Exterior Coffee', value: 'Exterior Coffee' },
    { label: 'Exterior Snack', value: 'Exterior Snack' },
    { label: 'Hydraulics Coffee', value: 'Hydraulics Coffee' },
    { label: 'Hydraulics Snack', value: 'Hydraulics Snack' },
    { label: 'Mechanical Coffee', value: 'Mechanical Coffee' },
    { label: 'Mechanical Snack', value: 'Mechanical Snack' }
];

const serviceAreaOptions = [
    { label: 'All Areas', value: null },
    { label: 'Downtown', value: 'Downtown' },
    { label: 'Uptown', value: 'Uptown' },
    { label: 'Midtown', value: 'Midtown' }
];

const routeOptions = ROUTE_OPTIONS;

const machineTypeOptions = [
    { label: 'All Types', value: null },
    { label: 'SnackMaster 5000', value: 'SnackMaster 5000' },
    { label: 'BeveragePro 3000', value: 'BeveragePro 3000' }
];

const formatMinutesMetric = (minutes, translate) => {
    if (minutes == null || Number.isNaN(minutes)) return translate('N/A');
    if (minutes < 60) return `${minutes} min`;
    const hours = (minutes / 60).toFixed(2);
    return `${hours} h`;
};

const formatPercentMetric = (value, translate) => {
    if (value == null || Number.isNaN(value)) return translate('N/A');
    return `${value}%`;
};

const TechnicianPerformanceDashboard = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [technicians, setTechnicians] = useState([]);
    const [selectedTechnician, setSelectedTechnician] = useState(null);
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const [assignmentSla, setAssignmentSla] = useState(null);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        failureType: null,
        serviceArea: null,
        route: null,
        technician: null,
        machineId: '',
        machineType: null,
        claimSlaMinutes: 15
    });

    const dispatch = useDispatch();

    useEffect(() => {
        // Load technicians for filter
        const loadTechnicians = async () => {
            try {
                const response = await handleGetRequest(endPoints.getTechnicians, true, false, dispatch);
                if (response?.data) {
                    const options = response.data.map((user) => ({
                        label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || t('Unknown User'),
                        value: user._id || user.id
                    }));
                    setTechnicianOptions([{ label: t('All Technicians'), value: null }, ...options]);
                }
            } catch (e) {
                console.error('Failed to load technicians', e);
            }
        };

        loadTechnicians();
    }, [dispatch, preferredLanguage]);

    const loadPerformance = async () => {
        const params = new URLSearchParams();
        if (filters.startDate) {
            params.append('from', filters.startDate.toISOString().split('T')[0]);
        }
        if (filters.endDate) {
            params.append('to', filters.endDate.toISOString().split('T')[0]);
        }
        if (filters.failureType) {
            params.append('typeOfFailure', filters.failureType);
        }
        if (filters.route) {
            params.append('route', filters.route);
        }
        if (filters.serviceArea) {
            params.append('serviceArea', filters.serviceArea);
        }
        if (filters.machineId) {
            params.append('machineId', filters.machineId);
        }
        if (filters.machineType) {
            params.append('machineType', filters.machineType);
        }
        if (filters.technician) {
            params.append('technicianId', filters.technician);
        }
        if (filters.claimSlaMinutes && Number(filters.claimSlaMinutes) > 0) {
            params.append('claimSlaMinutes', filters.claimSlaMinutes);
        }
        // Avoid stale 304 responses with empty payload in browser/XHR caches.
        params.append('_ts', Date.now().toString());

        const queryParams = params.toString();
        const response = await handleGetRequest(endPoints.getTechnicianPerformance(queryParams), true, false, dispatch);

        if (response?.data) {
            const payload = response.data;
            const apiTechs = Array.isArray(payload) ? payload : (payload?.technicians || []);
            setAssignmentSla(Array.isArray(payload) ? null : payload?.assignmentSLA || null);

            // Filter out "Unknown" entries - only show actual technicians
            const filteredTechs = apiTechs.filter((t) => {
                const name = t.technicianName || t.technician || '';
                return name !== 'Unknown' && name !== 'Unassigned' && t.technicianId != null;
            });

            const mapped = filteredTechs.map((t) => {
                const mttrMinutes = typeof t.mttrMinutes === 'number' ? t.mttrMinutes : null;
                const mttrHours = mttrMinutes != null ? (mttrMinutes / 60).toFixed(2) : 'N/A';

                return {
                    technicianId: t.technicianId || t._id || '',
                technicianName: t.technicianName || t.technician || translateUiText('Unknown', preferredLanguage),
                    mttr: mttrMinutes != null ? `${mttrHours} ${t('hours')}` : t('N/A'),
                    totalRepairs: t.totalRepairs ?? 0,
                    repairsByFailureType: (t.repairsByFailureType || []).map((r) => ({
                        type: r.type || r.typeOfFailure || 'Unknown',
                        count: r.count ?? 0
                    })),
                    actionsTaken: t.actionsTaken || [],
                    partsUsed: t.partsUsed || [],
                    movementsSummary: (t.movementsSummary || []).map((m) => ({
                        movementType: m.movementType || 'Unknown',
                        count: m.count ?? 0
                    })),
                    pmTasks: (t.pmTasks || []).map((p) => ({
                        category: p.category || 'Unknown',
                        completed: p.completed ?? 0,
                        missed: p.missed ?? 0
                    }))
                };
            });

            setTechnicians(mapped);
            if (mapped.length > 0 && !selectedTechnician) {
                setSelectedTechnician(mapped[0]);
            } else if (selectedTechnician) {
                // Update selected technician if it still exists
                const updated = mapped.find(t => t.technicianId === selectedTechnician.technicianId);
                if (updated) {
                    setSelectedTechnician(updated);
                } else if (mapped.length > 0) {
                    setSelectedTechnician(mapped[0]);
                }
            }
        }
    };

    useEffect(() => {
        loadPerformance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferredLanguage]);

    const applyFilters = () => {
        loadPerformance();
    };

    const resetFilters = () => {
        setFilters({
            startDate: null,
            endDate: null,
            failureType: null,
            serviceArea: null,
            route: null,
            technician: null,
            machineId: '',
            machineType: null,
            claimSlaMinutes: 15
        });
        setTimeout(() => {
            loadPerformance();
        }, 0);
    };

    const handleTechnicianSelect = (technician) => {
        setSelectedTechnician(technician);
    };

    return (
        <div>
            {/* Filters Section */}
            <div className="card mb-4">
                <h4 className="mb-3">{t('Filters')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Start Date')}</label>
                        <Calendar 
                            value={filters.startDate} 
                            onChange={(e) => setFilters({ ...filters, startDate: e.value })} 
                            dateFormat="yy-mm-dd" 
                            placeholder={t('Select Start Date')} 
                            showIcon 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('End Date')}</label>
                        <Calendar 
                            value={filters.endDate} 
                            onChange={(e) => setFilters({ ...filters, endDate: e.value })} 
                            dateFormat="yy-mm-dd" 
                            placeholder={t('Select End Date')} 
                            showIcon 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Failure Type')}</label>
                        <Dropdown 
                            value={filters.failureType} 
                            options={failureTypeOptions.map((option) => ({ ...option, label: t(option.label) }))} 
                            onChange={(e) => setFilters({ ...filters, failureType: e.value })} 
                            placeholder={t('Select Failure Type')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Service Area')}</label>
                        <Dropdown 
                            value={filters.serviceArea} 
                            options={serviceAreaOptions.map((option) => ({ ...option, label: t(option.label) }))} 
                            onChange={(e) => setFilters({ ...filters, serviceArea: e.value })} 
                            placeholder={t('Select Service Area')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Route')}</label>
                        <Dropdown 
                            value={filters.route} 
                            options={routeOptions} 
                            onChange={(e) => setFilters({ ...filters, route: e.value })} 
                            placeholder={t('Select Route')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Technician')}</label>
                        <Dropdown 
                            value={filters.technician} 
                            options={technicianOptions} 
                            onChange={(e) => setFilters({ ...filters, technician: e.value })} 
                            placeholder={t('Select Technician')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Machine ID')}</label>
                        <InputText 
                            value={filters.machineId} 
                            onChange={(e) => setFilters({ ...filters, machineId: e.target.value })} 
                            placeholder={t('Enter Machine ID')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Machine Type')}</label>
                        <Dropdown 
                            value={filters.machineType} 
                            options={machineTypeOptions.map((option) => ({ ...option, label: t(option.label) }))} 
                            onChange={(e) => setFilters({ ...filters, machineType: e.value })} 
                            placeholder={t('Select Machine Type')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Claim SLA (minutes)')}</label>
                        <InputText
                            type="number"
                            min={1}
                            value={filters.claimSlaMinutes}
                            onChange={(e) => setFilters({ ...filters, claimSlaMinutes: e.target.value })}
                            placeholder="15"
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <Button label={t('Apply Filters')} icon="pi pi-filter" onClick={applyFilters} className="p-button-primary" />
                    <Button label={t('Reset')} icon="pi pi-refresh" onClick={resetFilters} className="p-button-secondary" />
                </div>
            </div>

            <div className="grid mb-4">
                {technicians.map((tech) => (
                    <div key={tech.technicianId || tech.technicianName} className="col-12 md:col-4">
                        <Card
                            className={selectedTechnician?.technicianId === tech.technicianId ? 'border-primary' : ''}
                            onClick={() => handleTechnicianSelect(tech)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="text-center">
                                <h5>{tech.technicianName}</h5>
                                <div className="text-2xl font-bold text-primary mt-2">{tech.mttr}</div>
                                <div className="text-color-secondary">{t('Mean Time to Repair')}</div>
                                <div className="mt-3">
                                    <span className="text-lg font-bold">{tech.totalRepairs}</span>
                                    <span className="text-color-secondary ml-2">{t('Total Repairs')}</span>
                                </div>
                            </div>
                        </Card>
                    </div>
                ))}
            </div>

            {assignmentSla && (
                <div className="card mb-4">
                    <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h4 className="m-0">{t('Assignment SLA')}</h4>
                        <span className="text-color-secondary text-sm">
                            {t('Target:')} {assignmentSla.claimSlaMinutes || 15} {t('minutes')}
                        </span>
                    </div>

                    <div className="grid mb-3">
                        <div className="col-12 md:col-3">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-primary">
                                        {assignmentSla?.overview?.totalTickets ?? 0}
                                    </div>
                                    <div className="text-color-secondary">{t('Total Tickets')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-3">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#16a34a' }}>
                                        {formatPercentMetric(assignmentSla?.overview?.withinSlaPercent, t)}
                                    </div>
                                    <div className="text-color-secondary">{t('Claimed Within SLA')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-3">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#0f766e' }}>
                                        {formatMinutesMetric(assignmentSla?.overview?.avgTimeToClaimMinutes, t)}
                                    </div>
                                    <div className="text-color-secondary">{t('Avg Time to Claim')}</div>
                                </div>
                            </Card>
                        </div>
                        <div className="col-12 md:col-3">
                            <Card>
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: '#ea580c' }}>
                                        {assignmentSla?.overview?.unclaimedOpenTickets ?? 0}
                                    </div>
                                    <div className="text-color-secondary">{t('Open Unclaimed')}</div>
                                </div>
                            </Card>
                        </div>
                    </div>

                    <div className="grid">
                        <div className="col-12 lg:col-6">
                            <h5 className="mb-2">{t('By Route')}</h5>
                            <DataTable value={assignmentSla.byRoute || []} emptyMessage={t('No route data.')}>
                                <Column field="route" header={t('Route')} />
                                <Column field="totalTickets" header={t('Total')} />
                                <Column field="claimedTickets" header={t('Claimed')} />
                                <Column field="unclaimedOpenTickets" header={t('Unclaimed Open')} />
                                <Column
                                    field="avgTimeToClaimMinutes"
                                    header={t('Avg Claim')}
                                    body={(row) => formatMinutesMetric(row.avgTimeToClaimMinutes, t)}
                                />
                                <Column
                                    field="withinSlaPercent"
                                    header={t('Within SLA')}
                                    body={(row) => formatPercentMetric(row.withinSlaPercent, t)}
                                />
                            </DataTable>
                        </div>
                        <div className="col-12 lg:col-6">
                            <h5 className="mb-2">{t('By Team')}</h5>
                            <DataTable value={assignmentSla.byTeam || []} emptyMessage={t('No team data.')}>
                                <Column field="team" header={t('Team')} />
                                <Column field="totalTickets" header={t('Total')} />
                                <Column field="claimedTickets" header={t('Claimed')} />
                                <Column field="unclaimedOpenTickets" header={t('Unclaimed Open')} />
                                <Column
                                    field="avgTimeToClaimMinutes"
                                    header={t('Avg Claim')}
                                    body={(row) => formatMinutesMetric(row.avgTimeToClaimMinutes, t)}
                                />
                                <Column
                                    field="withinSlaPercent"
                                    header={t('Within SLA')}
                                    body={(row) => formatPercentMetric(row.withinSlaPercent, t)}
                                />
                            </DataTable>
                        </div>
                    </div>

                    <div className="mt-4">
                        <h5 className="mb-2">{t('Unclaimed Aging')}</h5>
                        <DataTable value={assignmentSla.unclaimedAging || []} emptyMessage={t('No unclaimed tickets.')}>
                            <Column field="orderCode" header={t('Order')} />
                            <Column field="route" header={t('Route')} />
                            <Column field="team" header={t('Team')} />
                            <Column field="machineCode" header={t('Machine')} />
                            <Column field="location" header={t('Location')} />
                            <Column
                                field="ageMinutes"
                                header={t('Age')}
                                body={(row) => formatMinutesMetric(row.ageMinutes, t)}
                            />
                            <Column
                                field="createdAt"
                                header={t('Created At')}
                                body={(row) => (row.createdAt ? new Date(row.createdAt).toLocaleString() : t('N/A'))}
                            />
                        </DataTable>
                    </div>
                </div>
            )}

            {selectedTechnician && (
                <div>
                    <div className="card mb-4">
                        <h4 className="mb-3">{t('Repairs by Failure Type')}</h4>
                        <DataTable value={selectedTechnician.repairsByFailureType} emptyMessage={t('No data available.')}>
                            <Column field="type" header={t('Failure Type')} body={(rowData) => t(rowData.type)} />
                            <Column field="count" header={t('Count')} />
                        </DataTable>
                    </div>

                    <div className="card mb-4">
                        <h4 className="mb-3">{t('Actions Taken')}</h4>
                        <ul>
                            {selectedTechnician.actionsTaken.length > 0 ? (
                                selectedTechnician.actionsTaken.map((action, index) => (
                                    <li key={index} className="mb-2">
                                        {action}
                                    </li>
                                ))
                            ) : (
                                <li className="text-color-secondary">{t('No actions recorded')}</li>
                            )}
                        </ul>
                    </div>

                    <div className="card mb-4">
                        <h4 className="mb-3">{t('Parts Used')}</h4>
                        <ul>
                            {selectedTechnician.partsUsed.length > 0 ? (
                                selectedTechnician.partsUsed.map((part, index) => (
                                    <li key={index} className="mb-2">
                                        {part}
                                    </li>
                                ))
                            ) : (
                                <li className="text-color-secondary">{t('No parts used')}</li>
                            )}
                        </ul>
                    </div>

                    <div className="card mb-4">
                        <h4 className="mb-3">{t('Machine Movements')}</h4>
                        <DataTable value={selectedTechnician.movementsSummary || []} emptyMessage={t('No data available.')}>
                            <Column field="movementType" header={t('Movement Type')} body={(rowData) => t(rowData.movementType)} />
                            <Column field="count" header={t('Count')} />
                        </DataTable>
                    </div>

                    <div className="card">
                        <h4 className="mb-3">{t('Preventive Maintenance Tasks')}</h4>
                        <DataTable value={selectedTechnician.pmTasks} emptyMessage={t('No data available.')}>
                            <Column field="category" header={t('Category')} body={(rowData) => t(rowData.category)} />
                            <Column field="completed" header={t('Completed')} />
                            <Column field="missed" header={t('Missed')} />
                        </DataTable>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TechnicianPerformanceDashboard;
